/*  ========================================================================
    # Sections - Calendar
    ========================================================================  */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FullCalendar from '@fullcalendar/react'; // eslint-disable-line
import dayGridPlugin from '@fullcalendar/daygrid'; // eslint-disable-line
import CalendarListEntry from './CalendarListEntry';

const Calendar = ({ entries }) => {
  // Sort the entries by date.
  const sortedEntries = entries.sort(
    (entryA, entryB) => new Date(entryA.start_date) - new Date(entryB.start_date)
  );

  // Map entries for the default (Calendar) tab.
  const mapCalendarEntries = entries => {
    const mappedEntries = [];
    entries.forEach(entry => {
      mappedEntries.push({
        title: entry.title,
        start: new Date(`${entry.start_date} ${entry.start_time}`),
        end: new Date(`${entry.end_date} ${entry.end_time}`),
        url: entry.url,
        backgroundColor: entry.categories[0].fields.background_color,
        borderColor: entry.categories[0].fields.background_color,
        textColor: entry.categories[0].fields.text_color
      });
    });
    return mappedEntries;
  };

  // Get available categories.
  let categories = [];
  sortedEntries.forEach(entry => {
    if (entry.categories) {
      entry.categories.forEach(cat => {
        categories.push(cat);
      });
    }
  });
  categories = categories.filter((v, i, a) => a.findIndex(v2 => v2.term_id === v.term_id) === i);

  // Current tab and entries.
  const [currentTab, setCurrentTab] = useState('default');
  const [calendarEntries, setCalendarEntries] = useState(mapCalendarEntries(sortedEntries));
  const [listEntries, setListEntries] = useState(sortedEntries);

  // Categories
  const categoriesSelectRef = useRef(null);
  const [categorySelected, setCategorySelected] = useState('');

  // Filter by Category
  useEffect(() => {
    if (categorySelected === '') {
      setCalendarEntries(mapCalendarEntries(sortedEntries));
      setListEntries(sortedEntries);
    } else {
      const filteredEntries = [];

      sortedEntries.forEach(entry => {
        if (entry.categories) {
          entry.categories.forEach(cat => {
            if (cat.term_id === parseInt(categorySelected, 10)) {
              filteredEntries.push(entry);
            }
          });
        }
      });

      setCalendarEntries(mapCalendarEntries(filteredEntries));
      setListEntries(filteredEntries);
    }
  }, [categorySelected]);

  return (
    <div className='centerMaxWidthContainer'>
      <div className='s-calendar__header'>
        <select
          ref={categoriesSelectRef}
          onChange={() => {
            setCategorySelected(categoriesSelectRef.current.value);
          }}
        >
          <option value={''}>Alle Kategorien</option>
          {categories.map((category, index) => (
            <option key={index} value={category.term_id}>
              {category.name}
            </option>
          ))}
        </select>
        <div className='s-calendar__tabs'>
          <button
            className={currentTab === 'default' ? 'active' : ''}
            onClick={() => {
              setCurrentTab('default');
            }}
          >
            Kalendar
          </button>
          <button
            className={currentTab === 'list' ? 'active' : ''}
            onClick={() => {
              setCurrentTab('list');
            }}
          >
            Liste
          </button>
        </div>
      </div>

      {/* Default */}
      {currentTab === 'default' && (
        <div className='s-calendar__tab s-calendar__default-tab'>
          <FullCalendar
            plugins={[dayGridPlugin]}
            locale='de'
            initialView='dayGridMonth'
            buttonText={{
              today: 'Heute'
            }}
            firstDay={1}
            weekends={true}
            displayEventTime={false}
            events={calendarEntries}
          />
        </div>
      )}

      {/* List */}
      {currentTab === 'list' && (
        <div className='s-calendar__tab s-calendar__list-tab'>
          {listEntries.length > 0 && (
            <>
              {listEntries.map((entry, index) => (
                <CalendarListEntry key={`${entry.title}-${index}`} {...entry} />
              ))}
            </>
          )}
          {listEntries.length === 0 && (
            <h3 style={{ textAlign: 'center' }}>Keine Termine oder Seminare vorhanden</h3>
          )}
        </div>
      )}
    </div>
  );
};

Calendar.propTypes = {
  entries: PropTypes.array
};

export default Calendar;
